// SPDX-License-Identifier: Apache-2.0

// EmailPreviewCard — specialised tool-card for `mail.*` results.
//
// Renders Gmail message / thread payloads as a subject-headed envelope
// preview. Click the snippet to expand to the full body. Attachments
// surface as chips that fire `mail.download_attachment` — but ONLY through
// the standard permission gate, since attachments may carry PII.
//
// Iris's progressive-enhancement rule: if the shape doesn't match what
// Gmail-MCP-Server's `read_email` / `search_emails` returns, fall back to
// a placeholder.

import { For, Show, createMemo, createSignal } from "solid-js";
import CommsSnapshotButton from "./CommsSnapshotButton";
import "./comms.css";

// ── Types — match GongRzhe/Gmail-MCP-Server's shapes ─────────────────

export interface MailAttachment {
  id?: string;
  attachmentId?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  /** Some servers populate a download URL directly; we ignore it and use the tool. */
  url?: string;
}

export interface MailMessage {
  id?: string;
  threadId?: string;
  subject?: string;
  from?: string;
  to?: string | string[];
  cc?: string | string[];
  date?: string;
  snippet?: string;
  body?: string;
  /** Gmail labels: e.g. INBOX, UNREAD, STARRED, IMPORTANT, CATEGORY_*. */
  labels?: string[];
  labelIds?: string[];
  attachments?: MailAttachment[];
  isUnread?: boolean;
}

export interface MailToolResult {
  display_hint?: "email-preview";
  // Single-message reads:
  message?: MailMessage;
  // search_emails result shape:
  messages?: MailMessage[];
  // Some forks return `emails`:
  emails?: MailMessage[];
  // Or just a top-level message blob with id+threadId+headers:
  id?: string;
  threadId?: string;
  subject?: string;
  from?: string;
  to?: string | string[];
  date?: string;
  snippet?: string;
  body?: string;
  labels?: string[];
  attachments?: MailAttachment[];
}

interface Props {
  result: unknown;
  toolName: string;
  /** When true, snapshot button is shown. Defaults to true. */
  showSnapshot?: boolean;
  /**
   * Hook for the chat-side caller to invoke `mail.download_attachment` once
   * the user confirms via the permission modal. The component calls this
   * with `{ messageId, attachmentId, filename }` — caller decides whether
   * to surface the modal and dispatch the tool. If undefined, attachment
   * chips render as disabled.
   */
  onDownloadAttachment?: (req: {
    messageId: string;
    attachmentId: string;
    filename: string;
  }) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────

function parseResult(raw: unknown): MailToolResult | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as MailToolResult;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as MailToolResult;
  return null;
}

/** Extract a flat list of messages from any of the shapes Gmail-MCP-Server returns. */
function extractMessages(p: MailToolResult | null): MailMessage[] {
  if (!p) return [];
  if (Array.isArray(p.messages)) return p.messages;
  if (Array.isArray(p.emails)) return p.emails;
  if (p.message) return [p.message];
  // Top-level inline message?
  if (p.subject || p.from || p.snippet || p.body || p.id) {
    return [
      {
        id: p.id,
        threadId: p.threadId,
        subject: p.subject,
        from: p.from,
        to: p.to,
        date: p.date,
        snippet: p.snippet,
        body: p.body,
        labels: p.labels,
        attachments: p.attachments,
      },
    ];
  }
  return [];
}

function addrAsString(a: string | string[] | undefined): string {
  if (!a) return "";
  if (typeof a === "string") return a;
  return a.join(", ");
}

function classifyLabel(label: string): string {
  const upper = label.toUpperCase();
  if (upper === "INBOX") return "mv-mail__label mv-mail__label--inbox";
  if (upper === "UNREAD") return "mv-mail__label mv-mail__label--unread";
  if (upper === "STARRED") return "mv-mail__label mv-mail__label--starred";
  if (upper === "IMPORTANT") return "mv-mail__label mv-mail__label--important";
  return "mv-mail__label";
}

function formatBytes(n: number | undefined): string {
  if (!n || !Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function dateHuman(s: string | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isUnread(m: MailMessage): boolean {
  if (m.isUnread) return true;
  const labels = (m.labels ?? m.labelIds ?? []).map((s) => s.toUpperCase());
  return labels.includes("UNREAD");
}

// ── Envelope SVG ─────────────────────────────────────────────────────

function EnvelopeIcon() {
  return (
    <svg
      class="mv-mail__envelope"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────

export default function EmailPreviewCard(props: Props) {
  const parsed = createMemo(() => parseResult(props.result));
  const messages = createMemo(() => extractMessages(parsed()));

  // Track per-message body expansion. Keyed by index since IDs are optional.
  const [expanded, setExpanded] = createSignal<Set<number>>(new Set());

  const toggle = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const headSubject = createMemo(() => {
    const ms = messages();
    if (ms.length === 0) return props.toolName;
    return ms[0]?.subject ?? "(no subject)";
  });

  const snapshotSlug = createMemo(() => {
    const subj = headSubject();
    return subj.replace(/[^a-zA-Z0-9-]+/g, "-").slice(0, 40).toLowerCase() || "email";
  });

  // Sensitivity default: inbox emails → private (per Iris research).
  const defaultSensitivity = createMemo<"private" | "internal">(() => "private");

  const snapshotMarkdown = createMemo(() => {
    const ms = messages();
    const lines: string[] = [];
    for (const m of ms) {
      lines.push(`### ${m.subject ?? "(no subject)"}`);
      lines.push("");
      if (m.from) lines.push(`**From:** ${m.from}`);
      if (m.to) lines.push(`**To:** ${addrAsString(m.to)}`);
      if (m.date) lines.push(`**Date:** ${dateHuman(m.date)}`);
      lines.push("");
      lines.push(m.body ?? m.snippet ?? "");
      if (m.attachments && m.attachments.length > 0) {
        lines.push("");
        lines.push("**Attachments:**");
        for (const a of m.attachments) {
          lines.push(`- ${a.filename ?? "attachment"} (${formatBytes(a.size)})`);
        }
      }
      lines.push("");
    }
    return lines.join("\n");
  });

  const fallback = (note: string) => (
    <div class="mv-comms">
      <div class="mv-comms__head">
        <span class="mv-comms__source mv-comms__source--mail">
          <span class="mv-comms__source-glyph">@</span>
          mail
        </span>
        <span class="mv-comms__title">{props.toolName}</span>
      </div>
      <div class="mv-comms__empty">{note}</div>
    </div>
  );

  return (
    <Show when={parsed()} fallback={fallback("loading…")}>
      <Show when={messages().length > 0} fallback={fallback("no messages")}>
        <div class="mv-comms mv-mail" data-tool-name={props.toolName}>
          <div class="mv-comms__head">
            <span class="mv-comms__source mv-comms__source--mail">
              <span class="mv-comms__source-glyph">@</span>
              mail
            </span>
            <span class="mv-comms__title">{headSubject()}</span>
            <Show when={messages().length > 1}>
              <span class="mv-comms__meta">{messages().length} messages</span>
            </Show>
          </div>

          <div class="mv-comms__body">
            <For each={messages()}>
              {(m, i) => {
                const expandedNow = () => expanded().has(i());
                const labels = m.labels ?? m.labelIds ?? [];
                return (
                  <div
                    class={`mv-mail__msg${isUnread(m) ? "" : " mv-mail--read"}`}
                  >
                    <div class="mv-mail__head">
                      <div class="mv-mail__subject">
                        <EnvelopeIcon />
                        <span>{m.subject ?? "(no subject)"}</span>
                      </div>
                      <div class="mv-mail__addrs">
                        <Show when={m.from}>
                          <span>
                            <span class="mv-mail__addr-label">from</span>
                            <span class="mv-mail__addr-value">{m.from}</span>
                          </span>
                        </Show>
                        <Show when={m.to}>
                          <span>
                            <span class="mv-mail__addr-label">to</span>
                            <span class="mv-mail__addr-value">
                              {addrAsString(m.to)}
                            </span>
                          </span>
                        </Show>
                        <Show when={m.date}>
                          <span>
                            <span class="mv-mail__addr-label">·</span>
                            <span class="mv-mail__addr-value">
                              {dateHuman(m.date)}
                            </span>
                          </span>
                        </Show>
                      </div>
                      <Show when={labels.length > 0}>
                        <div class="mv-mail__labels">
                          <For each={labels}>
                            {(l) => <span class={classifyLabel(l)}>{l}</span>}
                          </For>
                        </div>
                      </Show>
                    </div>

                    <Show when={m.snippet || m.body}>
                      <div
                        class={`mv-mail__snippet${expandedNow() ? "" : " mv-mail__snippet--collapsed"}`}
                        onClick={() => toggle(i())}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggle(i());
                          }
                        }}
                      >
                        {expandedNow() && m.body ? m.body : m.snippet ?? m.body}
                      </div>
                      <Show when={(m.body ?? "").length > (m.snippet ?? "").length}>
                        <div class="mv-mail__expand-hint">
                          {expandedNow() ? "click to collapse" : "click to expand"}
                        </div>
                      </Show>
                    </Show>

                    <Show when={m.attachments && m.attachments.length > 0}>
                      <div class="mv-mail__attachments">
                        <For each={m.attachments}>
                          {(a) => {
                            const canDownload =
                              !!props.onDownloadAttachment &&
                              !!(a.id ?? a.attachmentId) &&
                              !!m.id;
                            return (
                              <button
                                type="button"
                                class="mv-mail__attach"
                                disabled={!canDownload}
                                title={
                                  canDownload
                                    ? "download (will ask permission)"
                                    : "download unavailable"
                                }
                                onClick={() => {
                                  const messageId = m.id ?? "";
                                  const attachmentId = a.id ?? a.attachmentId ?? "";
                                  const filename = a.filename ?? "attachment";
                                  if (canDownload && props.onDownloadAttachment) {
                                    props.onDownloadAttachment({
                                      messageId,
                                      attachmentId,
                                      filename,
                                    });
                                  }
                                }}
                              >
                                <span class="mv-mail__attach-clip">📎</span>
                                <span>{a.filename ?? "attachment"}</span>
                                <Show when={a.size}>
                                  <span style={{ opacity: 0.7 }}>
                                    {formatBytes(a.size)}
                                  </span>
                                </Show>
                              </button>
                            );
                          }}
                        </For>
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>

          <Show when={props.showSnapshot !== false}>
            <div class="mv-comms__foot">
              <CommsSnapshotButton
                source="mail"
                title={`mail — ${headSubject()}`}
                slug={snapshotSlug()}
                content={snapshotMarkdown()}
                location={`inbox/comms/mail/`}
                defaultSensitivity={defaultSensitivity()}
                tags={["comms", "mail"]}
              />
              <span class="mv-comms-snapshot__where">
                → ~/brain/inbox/comms/mail/
              </span>
            </div>
          </Show>
        </div>
      </Show>
    </Show>
  );
}
